import { createContext, useCallback, useContext, useState } from 'react';
import ConfirmModal from '../components/shared/ConfirmModal';

const ModalContext = createContext(null);

export const ModalProvider = ({ children }) => {
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirmer',
    cancelText: 'Annuler',
    type: 'danger',
    icon: null,
    onConfirm: null,
    onCancel: null
  });
  const [isLoading, setIsLoading] = useState(false);

  const openModal = useCallback((config) => {
    setModalConfig({
      isOpen: true,
      title: '',
      message: '',
      confirmText: 'Confirmer',
      cancelText: 'Annuler',
      type: 'danger',
      icon: null,
      onConfirm: null,
      onCancel: null,
      ...config
    });
    setIsLoading(false);
  }, []);

  const closeModal = useCallback(() => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
    setIsLoading(false);
    if (modalConfig.onCancel) {
      modalConfig.onCancel();
    }
  }, [modalConfig]);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    
    if (modalConfig.onConfirm) {
      try {
        await modalConfig.onConfirm();
      } catch (error) {
        console.error('Error in modal confirm action:', error);
      }
    }
    
    setIsLoading(false);
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  }, [modalConfig]);

  // Helper methods for common modal types
  const confirmDelete = useCallback((config) => {
    openModal({
      title: 'Confirmer la suppression',
      message: config.itemName 
        ? `Êtes-vous sûr de vouloir supprimer "${config.itemName}" ? Cette action est irréversible.`
        : (config.message || 'Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.'),
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
      type: 'danger',
      icon: 'fa-trash-can',
      onConfirm: config.onConfirm,
      onCancel: config.onCancel
    });
  }, [openModal]);

  const confirmSave = useCallback((config) => {
    openModal({
      title: 'Confirmer les modifications',
      message: config.message || 'Voulez-vous enregistrer les modifications apportées ?',
      confirmText: 'Enregistrer',
      cancelText: 'Annuler',
      type: 'success',
      icon: 'fa-floppy-disk',
      onConfirm: config.onConfirm,
      onCancel: config.onCancel
    });
  }, [openModal]);

  const confirmPublish = useCallback((config) => {
    openModal({
      title: 'Confirmer la publication',
      message: config.itemName 
        ? `Publier "${config.itemName}" ? Il sera visible publiquement.`
        : (config.message || 'Cet élément sera visible publiquement. Voulez-vous continuer ?'),
      confirmText: 'Publier',
      cancelText: 'Annuler',
      type: 'success',
      icon: 'fa-upload',
      onConfirm: config.onConfirm,
      onCancel: config.onCancel
    });
  }, [openModal]);

  const confirmLogout = useCallback((config) => {
    openModal({
      title: 'Confirmer la déconnexion',
      message: config.message || 'Êtes-vous sûr de vouloir vous déconnecter ?',
      confirmText: 'Se déconnecter',
      cancelText: 'Rester connecté',
      type: 'warning',
      icon: 'fa-right-from-bracket',
      onConfirm: config.onConfirm,
      onCancel: config.onCancel
    });
  }, [openModal]);

  const value = {
    openModal,
    closeModal,
    confirmDelete,
    confirmSave,
    confirmPublish,
    confirmLogout
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <ConfirmModal
        isOpen={modalConfig.isOpen}
        onConfirm={handleConfirm}
        onCancel={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        confirmText={modalConfig.confirmText}
        cancelText={modalConfig.cancelText}
        type={modalConfig.type}
        icon={modalConfig.icon}
        isLoading={isLoading}
      />
    </ModalContext.Provider>
  );
};

export const useModal = () => {
  const ctx = useContext(ModalContext);
  if (!ctx) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return ctx;
};

export default ModalContext;
